
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

@Component({
  name: "KeywordSearch",
})
export default class extends Vue {
  @Prop({ default: () => [] }) value!: string[];
  @Prop({ default: "多个关键词用空格分隔" }) placeholder!: string;

  private keywords: string[] = [];
  private input = "";
  private focused = false;
  private composing = false;

  get activeTerms(): string[] {
    const draft = this.input.trim();
    return draft ? [...this.keywords, draft] : [...this.keywords];
  }

  @Watch("value", { immediate: true, deep: true })
  onValueChange(value: string[]) {
    if (!value.length && (this.keywords.length || this.input)) {
      this.resetInternal();
    }
  }

  @Watch("activeTerms", { deep: true })
  onActiveTermsChange(terms: string[]) {
    this.$emit("input", terms);
    this.$emit("change", terms);
  }

  public clear() {
    this.resetInternal();
    this.focusInput();
  }

  private resetInternal() {
    this.keywords = [];
    this.input = "";
    this.composing = false;
    const inputEl = this.$refs.searchInput as HTMLInputElement | undefined;
    if (inputEl) {
      inputEl.value = "";
    }
  }

  private getDraft(): string {
    const inputEl = this.$refs.searchInput as HTMLInputElement | undefined;
    return (inputEl?.value ?? this.input).trim();
  }

  private focusInput() {
    this.$nextTick(() => {
      (this.$refs.searchInput as HTMLInputElement | undefined)?.focus();
    });
  }

  private commitInput() {
    const value = this.getDraft();
    if (!value) {
      return;
    }
    this.keywords.push(value);
    this.input = "";
    const inputEl = this.$refs.searchInput as HTMLInputElement | undefined;
    if (inputEl) {
      inputEl.value = "";
    }
  }

  private handleCompositionStart() {
    this.composing = true;
  }

  private handleCompositionUpdate(event: CompositionEvent) {
    this.input = (event.target as HTMLInputElement).value;
  }

  private handleCompositionEnd(event: CompositionEvent) {
    this.composing = false;
    this.input = (event.target as HTMLInputElement).value;
  }

  private handleKeydown(event: KeyboardEvent) {
    if (event.isComposing || this.composing) {
      return;
    }
    if (event.key === "Backspace") {
      this.handleBackspace(event);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.commitInput();
    }
  }

  private handleInput() {
    const inputEl = this.$refs.searchInput as HTMLInputElement | undefined;
    if (inputEl) {
      this.input = inputEl.value;
    }
    if (this.composing) {
      return;
    }
    if (!this.input.includes(" ")) {
      return;
    }
    const parts = this.input.split(/\s+/);
    const completed = parts.slice(0, -1).map((part) => part.trim()).filter(Boolean);
    if (completed.length) {
      this.keywords.push(...completed);
    }
    this.input = parts[parts.length - 1] || "";
  }

  private handleBackspace(event: KeyboardEvent) {
    if (event.isComposing || this.composing) {
      return;
    }
    if (this.getDraft() || !this.keywords.length) {
      return;
    }
    event.preventDefault();
    this.keywords.pop();
  }

  private removeKeyword(index: number) {
    this.keywords.splice(index, 1);
    this.focusInput();
  }
}
